import * as Sentry from "@sentry/react";
import { lazy, memo, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import Section from './components/Layout/Section';
import { SENTRY_DSN } from './config';

// In dev, the Festival page have an issue with the 3D badge rendering
// due to the React.lazy and Suspense because of a re-render.
// import Festival from './views/Festival';

const Festival = lazy(() => import('./views/Festival'));
const Homepage = lazy(() => import('./views/Homepage'));
const Resources = lazy(() => import('./views/Resources'));
const Brickfilm = lazy(() => import('./views/Brickfilm'));
const Contests = lazy(() => import('./views/Contests'));
const CreateBrickfilm = lazy(() => import('./views/CreateBrickfilm'));
const EditBrickfilm = lazy(() => import('./views/EditBrickfilm'));
const Brickfilms = lazy(() => import('./views/Brickfilms'));
const SignIn = lazy(() => import('./views/Login'));
const Article = lazy(() => import('./views/Article'));
const CreateArticle = lazy(() => import('./views/CreateArticle'));
const EditArticle = lazy(() => import('./views/EditArticle'));
const Articles = lazy(() => import('./views/Articles'));
const User = lazy(() => import('./views/User'));
const Resource = lazy(() => import('./views/Resource'));
const EditResource = lazy(() => import('./views/EditResource'));
const CreateResource = lazy(() => import('./views/CreateResource'));
const EditUser = lazy(() => import('./views/EditUser'));
const UserDebug = lazy(() => import('./views/UserDebug'));
const BBcode = lazy(() => import('./views/AdminBBcode'));
const Rules = lazy(() => import('./views/Rules'));
const Community = lazy(() => import('./views/Community'));
const Thread = lazy(() => import('./views/Thread'));
const Notifications = lazy(() => import('./views/Notifications'));
const NotificationRedirect = lazy(() => import('./views/NotificationRedirect'));
const Chats = lazy(() => import('./views/Chats'));
const Team = lazy(() => import('./views/Team'));
const Users = lazy(() => import('./views/Users'));
const EditThread = lazy(() => import('./views/EditThread'));
const CreateThread = lazy(() => import('./views/CreateThread'));
const Contest = lazy(() => import('./views/Contest'));
const EditContest = lazy(() => import('./views/EditContest'));
const CreateContest = lazy(() => import('./views/CreateContest'));
const ContestJudgeList = lazy(() => import('./views/ContestJudgeList'));
const ContestJudgeVote = lazy(() => import('./views/ContestJudgeVote'));
const NotFound = lazy(() => import('./views/NotFound'));
const AdminAppflowy = lazy(() => import('./views/AdminAppflowy'));
const AdminAccounts = lazy(() => import('./views/AdminAccounts'));
const AdminCreateNotification = lazy(() => import('./views/AdminCreateNotification'));
const AdminSendEmail = lazy(() => import('./views/AdminSendEmail'));
const AdminLeads = lazy(() => import('./views/AdminLeads'));
const AdminSendBadge = lazy(() => import('./views/AdminSendBadge'));
const AdminMergeBrickfilms = lazy(() => import('./views/AdminMergeBrickfilms'));
const AdminMergeUsers = lazy(() => import('./views/AdminMergeUsers'));
const AdminBrickfilms = lazy(() => import('./views/AdminBrickfilms'));
const AdminOpeningVideos = lazy(() => import('./views/AdminOpeningVideos'));
const AdminUploadBackupedBrickfilms = lazy(() => import('./views/AdminUploadBrickfilms'));
const AdminTutorials = lazy(() => import('./views/AdminTutorials'));
const AdminScanner = lazy(() => import('./views/AdminScanner'));
const Admin = lazy(() => import('./views/Admin'));
const Stats = lazy(() => import('./views/Stats'));
const Offers = lazy(() => import('./views/Offers'));
const Events = lazy(() => import('./views/Events'));
const Calendar = lazy(() => import('./views/Calendar'));
const CreateCalendarEvent = lazy(() => import('./views/CreateCalendarEvent'));
const EditCalendarEvent = lazy(() => import('./views/EditCalendarEvent'));
const AboutUs = lazy(() => import('./views/AboutUs'));
const ShareYourPassion = lazy(() => import('./views/ShareYourPassion'));
const LegalNotices = lazy(() => import('./views/LegalNotices'));
const Experimental = lazy(() => import('./views/Experimental'));
const Generator = lazy(() => import('./views/Generator'));
const EagleAnimation = lazy(() => import('./views/EagleAnimation'));
const AudioLibrary = lazy(() => import('./views/AudioLibrary'));
const Map = lazy(() => import('./views/Map'));
const Survey2024 = lazy(() => import('./views/Survey2024'));
const Event = lazy(() => import('./views/Event'));
const AllExhibitions = lazy(() => import('./views/Exhibitions'));
const ExhibitionsMap = lazy(() => import('./views/ExhibitionsMap'));
const CreateEvent = lazy(() => import('./views/CreateEvent'));
const EditEvent = lazy(() => import('./views/EditEvent'));
const ExhibitionVideo = lazy(() => import('./views/ExhibitionVideo'));
const Order = lazy(() => import('./views/Order'));
const EasterScoreBoard = lazy(() => import('./views/EasterScoreBoard'));
const Build = lazy(() => import('./views/Build'));
const AccountRemoval = lazy(() => import('./views/AccountRemoval'));
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy'));
const AdminStaffTracking = lazy(() => import('./views/AdminStaffTracking'));

// If you add a public route, also add it in the API for URL parsing and SEO
// api:src/core/meta.js
export const ROUTES = {
    Home: {
        path: () => '/',
        Component: Homepage,
        disableSection: true,
        disableDesktopBreadcrumb: true,
    },
    AllExhibitions: {
        path: () => `/events/all`,
        Component: AllExhibitions,
    },
    ExhibitionsMap: {
        path: () => `/events/map`,
        Component: ExhibitionsMap,
    },
    CreateEvent: {
        path: () => `/events/create`,
        Component: CreateEvent,
    },
    EditEvent: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/events/${id}/${slug}/edit`,
        Component: EditEvent,
    },
    EventVideo: {
        path: ({ id = ':id', videoId = ':videoId', slug = ':slug' } = {}) => `/events/${id}/${videoId}/${slug}`,
        Component: ExhibitionVideo,
    },
    Event: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/events/${id}/${slug}`,
        Component: Event,
    },
    Articles: {
        path: () => `/news/`,
        Component: Articles,
        tab: 'ARTICLE',
    },
    Article: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/news/${id}/${slug}`,
        Component: Article,
        tab: 'ARTICLE',
    },
    Festival: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/festivals/${id}/${slug}`,
        Component: Festival,
        disableSection: true,
        disableDesktopBreadcrumb: true,
    },
    CreateArticle: {
        path: () => '/news/create',
        Component: CreateArticle,
        tab: 'ARTICLE',
    },
    EditArticle: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/news/${id}/${slug}/edit`,
        Component: EditArticle,
        tab: 'ARTICLE',
    },
    User: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/users/${id}/${slug}`,
        Component: User,
        tab: 'COMMUNITY',
    },
    EditUser: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/users/${id}/${slug}/edit`,
        Component: EditUser,
        tab: 'COMMUNITY',
    },
    UserDebug: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/users/${id}/${slug}/debug`,
        Component: UserDebug,
        tab: 'COMMUNITY',
    },
    Notifications: {
        path: () => `/notifications/`,
        Component: Notifications,
        tab: 'NOTIFICATION',
    },
    NotificationRedirect: {
        path: ({ id = ':id' } = {}) => `/notifications/${id}/`,
        Component: NotificationRedirect,
        tab: 'NOTIFICATION',
        disableDesktopBreadcrumb: true,
    },
    ChatMessages: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/chats/${id}/${slug}`,
        Component: Chats,
        tab: 'CHAT',
    },
    Chats: {
        path: () => `/chats/`,
        Component: Chats,
        tab: 'CHAT',
    },
    Brickfilm: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/brickfilms/${id}/${slug}`,
        Component: Brickfilm,
        tab: 'BRICKFILM',
    },
    CreateBrickfilm: {
        path: () => `/brickfilms/create`,
        Component: CreateBrickfilm,
        tab: 'BRICKFILM',
    },
    EditBrickfilm: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/brickfilms/${id}/${slug}/edit`,
        Component: EditBrickfilm,
        tab: 'BRICKFILM',
    },
    Brickfilms: {
        path: () => `/brickfilms/`,
        Component: Brickfilms,
        tab: 'BRICKFILM',
    },
    SignIn: {
        path: () => `/sign-in/`,
        Component: SignIn,
        tab: 'AUTH',
    },
    SignUp: {
        path: () => `/sign-up/`,
        Component: SignIn,
        tab: 'AUTH',
    },
    PasswordRecovery: {
        path: () => `/password-recovery/`,
        Component: SignIn,
        tab: 'AUTH',
    },
    BBcode: {
        path: () => `/admin/bbcode/`,
        Component: BBcode,
        tab: 'ADMIN',
    },
    AdminTutorials: {
        path: () => `/admin/tutorials/`,
        Component: AdminTutorials,
        tab: 'ADMIN',
    },
    Rules: {
        path: () => `/rules/`,
        Component: Rules,
        tab: 'PAGE',
    },
    Community: {
        path: () => `/community/`,
        Component: Community,
        tab: 'COMMUNITY',
    },
    Thread: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/community/${id}/${slug}`,
        Component: Thread,
        tab: 'COMMUNITY',
    },
    EditThread: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/community/${id}/${slug}/edit`,
        Component: EditThread,
        tab: 'COMMUNITY',
    },
    CreateThread: {
        path: () => `/community/create`,
        Component: CreateThread,
        tab: 'COMMUNITY',
    },
    CreateResource: {
        path: () => `/resources/create`,
        Component: CreateResource,
        tab: 'RESOURCE',
    },
    Resource: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/resources/${id}/${slug}`,
        Component: Resource,
        tab: 'RESOURCE',
    },
    EditResource: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/resources/${id}/${slug}/edit`,
        Component: EditResource,
        tab: 'RESOURCE',
    },
    Resources: {
        path: () => `/resources/`,
        Component: Resources,
        tab: 'RESOURCE',
    },
    Team: {
        path: () => `/team/`,
        Component: Team,
        tab: 'PAGE',
    },
    Users: {
        path: () => `/users/`,
        Component: Users,
        tab: 'RESOURCE',
    },
    Events: {
        path: () => `/events/`,
        Component: Events,
        tab: 'PAGE',
    },
    ContestJudgeVote: {
        path: ({ id = ':id', judgeId = ':judgeId', brickfilmId = ':brickfilmId' } = {}) => `/contests/${id}/judge/${judgeId}/brickfilm/${brickfilmId}`,
        Component: ContestJudgeVote,
        tab: 'BRICKFILM',
    },
    ContestJudgeList: {
        path: ({ id = ':id', judgeId = ':judgeId' } = {}) => `/contests/${id}/judge/${judgeId}`,
        Component: ContestJudgeList,
        tab: 'BRICKFILM',
    },
    Contest: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/contests/${id}/${slug}`,
        Component: Contest,
        tab: 'BRICKFILM',
    },
    Contests: {
        path: () => `/contests`,
        Component: Contests,
        tab: 'BRICKFILM',
    },
    EditContest: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/contests/${id}/${slug}/edit`,
        Component: EditContest,
        tab: 'BRICKFILM',
    },
    CreateContest: {
        path: () => `/contests/create`,
        Component: CreateContest,
        tab: 'BRICKFILM',
    },
    AdminAccounts: {
        path: () => `/admin/accounts`,
        Component: AdminAccounts,
        tab: 'ADMIN',
    },
    AdminLeads: {
        path: () => `/admin/leads`,
        Component: AdminLeads,
        tab: 'ADMIN',
    },
    AdminSendEmail: {
        path: () => `/admin/email`,
        Component: AdminSendEmail,
        tab: 'ADMIN',
    },
    AdminCreateNotification: {
        path: () => `/admin/notification`,
        Component: AdminCreateNotification,
        tab: 'ADMIN',
    },
    AdminSendBadge: {
        path: () => `/admin/badge`,
        Component: AdminSendBadge,
        tab: 'ADMIN',
    },
    AdminOpeningVideos: {
        path: () => '/admin/generator',
        Component: AdminOpeningVideos,
        tab: 'ADMIN',
    },
    AdminMergeBrickfilms: {
        path: () => `/admin/merge-brickfilms`,
        Component: AdminMergeBrickfilms,
        tab: 'ADMIN',
    },
    AdminAppflowy: {
        path: () => `/admin/appflowy`,
        Component: AdminAppflowy,
        tab: 'ADMIN',
        disableDesktopBreadcrumb: true,
    },
    AdminMergeUsers: {
        path: () => `/admin/merge-users`,
        Component: AdminMergeUsers,
        tab: 'ADMIN',
    },
    AdminBrickfilms: {
        path: () => `/admin/brickfilms`,
        Component: AdminBrickfilms,
        tab: 'ADMIN',
    },
    AdminUploadBackupedBrickfilms: {
        path: () => `/admin/upload-brickfilms`,
        Component: AdminUploadBackupedBrickfilms,
        tab: 'ADMIN',
    },
    AdminScanner: {
        path: () => `/admin/scanner`,
        Component: AdminScanner,
        tab: 'ADMIN',
    },
    Admin: {
        path: () => `/admin/`,
        Component: Admin,
        tab: 'ADMIN',
    },
    AdminStaffActivity: {
        path: () => `/admin/staff-activity`,
        Component: AdminStaffTracking,
        tab: 'ADMIN',
    },
    Stats: {
        path: () => `/stats/`,
        Component: Stats,
        tab: 'PAGE',
    },
    Offers: {
        path: () => `/offers/`,
        Component: Offers,
        tab: 'PAGE',
    },
    Map: {
        path: () => `/map/`,
        Component: Map,
        tab: 'PAGE',
    },
    Calendar: {
        path: () => `/calendar/`,
        Component: Calendar,
        tab: 'ARTICLE',
    },
    CreateCalendarEvent: {
        path: () => '/calendar/create',
        Component: CreateCalendarEvent,
        tab: 'ARTICLE',
    },
    EditCalendarEvent: {
        path: ({ id = ':id', slug = ':slug' } = {}) => `/calendar/${id}/${slug}/edit`,
        Component: EditCalendarEvent,
        tab: 'ARTICLE',
    },
    Order: {
        path: ({ id = ':id' } = {}) => `/orders/${id}`,
        Component: Order,
        tab: 'COMMUNITY',
    },
    AboutUs: {
        path: () => `/about-us`,
        Component: AboutUs,
        tab: 'PAGE',
    },
    ShareYourPassion: {
        path: () => `/events/share-your-passion`,
        Component: ShareYourPassion,
        tab: 'PAGE',
    },
    LegalNotices: {
        path: () => `/legal-notices`,
        Component: LegalNotices,
        tab: 'PAGE',
    },
    Experimental: {
        path: () => `/experimental`,
        Component: Experimental,
        tab: 'PAGE',
    },
    Generator: {
        path: () => `/generator`,
        Component: Generator,
        tab: 'RESOURCE',
    },
    EagleAnimation: {
        path: () => `/eagle-animation`,
        Component: EagleAnimation,
        disableSection: true,
        disableDesktopBreadcrumb: true,
        tab: 'RESOURCE',
    },
    AudioLibrary: {
        path: () => `/audio-library`,
        Component: AudioLibrary,
        tab: 'RESOURCE',
    },
    Survey2024: {
        path: () => `/survey2024`,
        Component: Survey2024,
        tab: 'RESOURCE',
        disableDesktopBreadcrumb: true,
    },
    Easter: {
        path: (year = `:year`) => `/easter/${year}`,
        Component: EasterScoreBoard,
        tab: 'OTHER',
    },
    Build: {
        path: () => `/build`,
        Component: Build,
        tab: 'OTHER',
    },
    AccountRemoval: {
        path: () => `/account-removal`,
        Component: AccountRemoval,
        tab: 'PAGE',
    },
    PrivacyPolicy: {
        path: () => `/privacy-policy`,
        Component: PrivacyPolicy,
        tab: 'PAGE',
    }
};
//console.log(JSON.stringify(Object.keys(ROUTES).reduce((acc, e) => ({ ...acc, [e]: ROUTES[e].path() }), {}), null, 2)); // For backend use

const Wrapper = memo(({ disableSection = false, children }) => (disableSection ? children : <Section>{children}</Section>)); // eslint-disable-line react/display-name
const RoutesList = Object.keys(ROUTES).map(route => ({ id: route, ...ROUTES[route] }));

const SentryRoutes = SENTRY_DSN ? Sentry.withSentryReactRouterV6Routing(Routes) : Routes;

export default function RouteDispatcher() {
    return (
        <SentryRoutes>
            {RoutesList.map((route) => {
                const id = route?.id;
                const url = route?.path();
                const Component = route?.Component || null;
                const disableSection = route?.disableSection || false;

                return (
                    <Route exact key={url} path={url} element={<Wrapper key={url} disableSection={disableSection}>
                        {typeof Component === 'object' && <Suspense fallback={null}>
                            <Component routeId={id} />
                        </Suspense>}
                        {typeof Component === 'function' && <Component routeId={id} />}
                    </Wrapper>} />
                )
            })}
            <Route path="*" element={<Wrapper>
                <NotFound />
            </Wrapper>} />
        </SentryRoutes>);
}
